import { useCallback, useEffect, useState } from 'react'
import { addDeviceToken, removeDeviceToken, useGetDeviceTokens } from '@/api/deviceToken'
import { IS_MOBILE_APP } from '@/constants'
import { MobileAppBridge } from '@/utils/mobileAppActions'
import toast from '@/utils/toast'


const usePushNotifications = ({
  afterDelete,
}: {
  afterDelete?: () => void
}) => {
  // Device Token of this device
  const [myDeviceToken, setMyDeviceToken] = useState<string>()
  const [myDeviceTokenLoading, setMyDeviceTokenLoading] = useState(IS_MOBILE_APP)

  const {
    data: deviceTokens,
    refetch: refetchDeviceTokens,
    isLoading: isLoadingDeviceTokens,
  } = useGetDeviceTokens()
  const devices = deviceTokens?.data || []
  const myDeviceIsRegistered = myDeviceToken ? devices.some(device => device.token === myDeviceToken) : false

  // Only runs once, and only in mobile app
  useEffect(() => {
    if (!IS_MOBILE_APP) return

    MobileAppBridge.getCurrentExpoPushToken().then(token => {
      setMyDeviceToken(token ?? undefined)
    }).catch(() => {
      setMyDeviceToken(undefined)
    }).finally(() => {
      setMyDeviceTokenLoading(false)
    })
  }, [])

  const addNewDeviceToken = useCallback(async () => {
    if (!IS_MOBILE_APP) return // User should not be able to add device token if not in mobile app

    const token = await MobileAppBridge.requestExpoPushToken()
    if (!token) {
      toast.error('Failed to obtain push notification token.')
      return
    }

    try {
      await addDeviceToken(token)
      setMyDeviceToken(token)
      toast.success('Device added for push notifications.')
    } catch {
      toast.error('Failed to add device token.')
    } finally {
      await refetchDeviceTokens()
    }
  }, [refetchDeviceTokens])

  const handleRemoveDeviceToken = useCallback(async (deviceTokenId: string) => {
    try {
      await removeDeviceToken(deviceTokenId)
      toast.success('Device removed from push notifications.')
    } catch {
      toast.error('Failed to remove device token.')
    } finally {
      afterDelete?.()
      await refetchDeviceTokens()
    }
  }, [afterDelete, refetchDeviceTokens])

  return {
    loading: isLoadingDeviceTokens || myDeviceTokenLoading,
    devices,
    myDeviceToken,
    myDeviceIsRegistered,
    addNewDeviceToken,
    handleRemoveDeviceToken,
  }
}

export default usePushNotifications
